<template>
  <div class="grade-page">
    <van-nav-bar
      :title="$t('m.dj_span0')"
      fixed
      placeholder
      :border="false"
      left-arrow
      @click-left="onClickLeft"
    />

    <div class="grade-shell">
      <section class="grade-hero">
        <div class="hero-top">
          <div class="avatar-mark">
            <span>{{ heroBadgeText }}</span>
          </div>
          <div class="hero-user">
            <p>HI, {{ displayName }}</p>
            <strong>{{ activeHeroTitle }}</strong>
          </div>
          <div class="hero-chip">{{ activeTabLabel }}</div>
        </div>

        <div class="hero-metrics" v-if="activeTab === 'member'">
          <div>
            <span>当前等级</span>
            <strong>{{ currentMemberName }}</strong>
          </div>
          <div>
            <span>累计积分</span>
            <strong>{{ formatInteger(memberTotalPoints) }}</strong>
          </div>
          <div>
            <span>加息率</span>
            <strong>{{ formatPercent(currentMemberRate) }}%</strong>
          </div>
        </div>

        <div class="hero-metrics" v-else>
          <div>
            <span>团队人数</span>
            <strong>{{ formatInteger(teamStats.people) }}</strong>
          </div>
          <div>
            <span>已购金额</span>
            <strong>{{ formatMoneyCompact(teamStats.hold) }}</strong>
          </div>
          <div>
            <span>月奖励</span>
            <strong>{{ formatMoneyCompact(currentTeamReward) }}</strong>
          </div>
        </div>

        <div v-if="activeTab === 'member'" class="hero-reward">
          <div>
            <span>每日签到奖励</span>
            <strong>{{ formatMoney(currentMemberSignReward) }} USD</strong>
          </div>
          <em>签到后自动派发到余额</em>
        </div>

        <div class="progress-wrap">
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: activeProgress + '%' }"></div>
          </div>
          <div class="progress-copy">{{ activeProgressText }}</div>
        </div>
      </section>

      <div class="grade-tabs">
        <button
          type="button"
          :class="{ active: activeTab === 'member' }"
          @click="switchTab('member')"
        >
          会员等级说明
        </button>
        <button
          type="button"
          :class="{ active: activeTab === 'team' }"
          @click="switchTab('team')"
        >
          团队等级说明
        </button>
      </div>

      <section v-if="activeTab === 'member'" class="commission-panel">
        <div class="commission-head">
          <div>
            <span>三级分佣体系</span>
            <strong>邀请会员购买产品后，1代、2代、3代佣金实时结算</strong>
          </div>
          <em>USD</em>
        </div>
        <div class="commission-steps">
          <div
            v-for="item in commissionRulesDisplay"
            :key="item.level"
            class="commission-step"
          >
            <span>{{ item.label }}</span>
            <strong>{{ formatPercent(item.rate) }}%</strong>
            <em>{{ item.desc }}</em>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'member'" class="grade-panel">
        <div class="panel-head">
          <div>
            <p>会员等级</p>
            <strong>共 {{ memberLevels.length }} 档，购买积分越高，会员加息率和签到奖励越高</strong>
          </div>
        </div>

        <div v-if="loading.member" class="empty-state">
          <van-loading size="18px" color="#0d7df2" />
          <span>加载中</span>
        </div>
        <div v-else class="member-list">
          <div
            v-for="item in memberLevels"
            :key="item.id || item.level"
            class="member-row"
            :class="{
              current: isCurrentMember(item),
              reached: isMemberReached(item)
            }"
          >
            <div class="level-badge">{{ displayBadgeName(item) }}</div>
            <div class="level-info">
              <strong>{{ displayLevelName(item) }}</strong>
              <span>累计购买积分达到 {{ formatInteger(getMemberBuildPoints(item)) }}</span>
            </div>
            <div class="level-sign-highlight">
              <span>每日签到奖励</span>
              <strong>{{ formatMoney(getMemberSignReward(item)) }} <em>USD</em></strong>
            </div>
            <div class="level-benefits">
              <div class="benefit-cell rate-cell">
                <span>日化回报率</span>
                <strong>+{{ formatPercent(item.rate) }}%</strong>
              </div>
              <div class="benefit-cell points-cell">
                <span>购买积分</span>
                <strong>{{ formatInteger(getMemberBuildPoints(item)) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-else class="grade-panel">
        <div class="panel-head">
          <div>
            <p>团队等级</p>
            <strong>共 {{ normalizedTeamLevels.length }} 档，按团队人数与团队购买金额自动匹配</strong>
          </div>
        </div>

        <div v-if="loading.team" class="empty-state">
          <van-loading size="18px" color="#0d7df2" />
          <span>加载中</span>
        </div>
        <template v-else>
          <div class="team-summary">
            <div>
              <span>距下一等级人数</span>
              <strong>{{ formatInteger(teamGap.people) }}</strong>
            </div>
            <div>
              <span>距下一等级购买额</span>
              <strong>{{ formatMoney(teamGap.hold) }} USD</strong>
            </div>
          </div>

          <div class="team-list">
            <div
              v-for="item in normalizedTeamLevels"
              :key="item.level"
              class="team-rule"
              :class="{
                current: isCurrentTeam(item),
                reached: isTeamReached(item)
              }"
            >
              <div class="rule-main">
                <div class="rule-index">{{ item.level }}</div>
                <div>
                  <strong>{{ item.name }}</strong>
                  <span>{{ teamRuleStatus(item) }}</span>
                </div>
              </div>
              <div class="rule-grid">
                <div>
                  <span>团队人数</span>
                  <strong>{{ formatInteger(item.people) }}</strong>
                </div>
                <div>
                  <span>购买金额</span>
                  <strong>{{ formatMoneyCompact(item.hold) }} USD</strong>
                </div>
                <div>
                  <span>每月奖励</span>
                  <strong>{{ formatMoneyCompact(item.reward) }} USD</strong>
                </div>
              </div>
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import { getMyUserInfo } from "@api/user";
import request from "@utils/request";

const FALLBACK_TEAM_LEVELS = [
  { level: 1, name: "团队等级一", people: 3, hold: 10000, reward: 288.88 },
  { level: 2, name: "团队等级二", people: 10, hold: 50000, reward: 1888.88 },
  { level: 3, name: "团队等级三", people: 20, hold: 100000, reward: 5088.88 },
  { level: 4, name: "团队等级四", people: 30, hold: 300000, reward: 10888.88 },
  { level: 5, name: "团队等级五", people: 50, hold: 1500000, reward: 188888.88 },
  { level: 6, name: "团队等级六", people: 100, hold: 5000000, reward: 588888.88 }
];

const FALLBACK_COMMISSION_RULES = [
  { level: 1, label: "1代", rate: 5, desc: "直推会员购买" },
  { level: 2, label: "2代", rate: 3, desc: "下级邀请购买" },
  { level: 3, label: "3代", rate: 2, desc: "三级团队购买" }
];

function toNumber(value) {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}

function toOptionalNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getErrorMessage(err, fallback) {
  if (err && err.response && err.response.data && err.response.data.msg) {
    return err.response.data.msg;
  }
  if (err && err.msg) {
    return err.msg;
  }
  return fallback;
}

export default {
  name: "Grade",
  data() {
    return {
      activeTab: "member",
      userInfo: {},
      memberLevels: [],
      teamLevels: FALLBACK_TEAM_LEVELS,
      teamSummary: {
        people: 0,
        hold: 0,
        current_level: null,
        next_level: FALLBACK_TEAM_LEVELS[0],
        gap: {
          people: FALLBACK_TEAM_LEVELS[0].people,
          hold: FALLBACK_TEAM_LEVELS[0].hold
        }
      },
      commissionRules: FALLBACK_COMMISSION_RULES,
      loading: {
        member: false,
        team: false,
        user: false
      }
    };
  },
  computed: {
    displayName() {
      return (
        this.userInfo.realname ||
        this.userInfo.nickname ||
        this.userInfo.username ||
        this.userInfo.mobile ||
        "会员"
      );
    },
    activeTabLabel() {
      return this.activeTab === "member" ? "会员等级" : "团队等级";
    },
    heroBadgeText() {
      if (this.activeTab === "team") {
        return `T${this.currentTeamLevel ? toNumber(this.currentTeamLevel.level) : 0}`;
      }
      return `V${this.currentMemberNumber}`;
    },
    activeHeroTitle() {
      if (this.activeTab === "member") {
        if (!this.nextMemberLevel) {
          return "已达到最高会员等级";
        }
        return this.memberPointGap > 0
          ? `距离 ${this.displayLevelName(this.nextMemberLevel)} 还差 ${this.formatInteger(this.memberPointGap)} 积分`
          : `已达到 ${this.displayLevelName(this.nextMemberLevel)} 积分门槛`;
      }
      return this.nextTeamLevel
        ? `距离 ${this.nextTeamLevel.name} 还差 ${this.teamGapText}`
        : "已达到最高团队等级";
    },
    memberVipNumber() {
      const fields = [
        this.userInfo.vip_level,
        this.userInfo.vip,
        this.userInfo.level_id
      ];
      for (let i = 0; i < fields.length; i++) {
        const value = toOptionalNumber(fields[i]);
        if (value !== null) {
          return value;
        }
      }
      const numericLevel = toOptionalNumber(this.userInfo.level);
      if (numericLevel !== null) {
        return numericLevel;
      }
      const levelName = this.userInfo.level_name || this.userInfo.level;
      const byName = this.memberLevels.find(item => {
        return levelName && this.displayLevelName(item) === String(levelName).trim();
      });
      return byName ? toNumber(byName.level) : null;
    },
    currentMemberNumber() {
      if (this.memberVipNumber !== null) {
        return this.memberVipNumber;
      }
      const reached = this.memberLevels
        .filter(item => this.memberTotalPoints >= toNumber(item.integral))
        .pop();
      return reached ? toNumber(reached.level) : 0;
    },
    memberTotalPoints() {
      const totalPoints = toNumber(this.userInfo.integrals);
      return totalPoints > 0 ? totalPoints : toNumber(this.userInfo.integral);
    },
    currentMemberLevel() {
      const byLevel = this.memberLevels.find(item => {
        return toNumber(item.level) === this.currentMemberNumber;
      });
      if (byLevel) {
        return byLevel;
      }
      const reached = this.memberLevels
        .filter(item => this.memberTotalPoints >= toNumber(item.integral))
        .pop();
      return reached || null;
    },
    currentMemberName() {
      return this.currentMemberLevel
        ? this.displayLevelName(this.currentMemberLevel)
        : `VIP${this.currentMemberNumber}`;
    },
    currentMemberRate() {
      return this.currentMemberLevel ? toNumber(this.currentMemberLevel.rate) : 0;
    },
    currentMemberSignReward() {
      return this.currentMemberLevel ? this.getMemberSignReward(this.currentMemberLevel) : 0;
    },
    commissionRulesDisplay() {
      return (this.commissionRules || FALLBACK_COMMISSION_RULES).map(item => {
        return {
          level: toNumber(item.level),
          label: item.label,
          rate: toNumber(item.rate),
          desc: item.desc
        };
      });
    },
    nextMemberLevel() {
      const nextByLevel = this.memberLevels.find(item => {
        return toNumber(item.level) > this.currentMemberNumber;
      });
      if (nextByLevel) {
        return nextByLevel;
      }
      return null;
    },
    memberPointGap() {
      if (!this.nextMemberLevel) {
        return 0;
      }
      return Math.max(0, toNumber(this.nextMemberLevel.integral) - this.memberTotalPoints);
    },
    memberProgress() {
      if (!this.nextMemberLevel) {
        return 100;
      }
      const currentLimit = this.currentMemberLevel
        ? toNumber(this.currentMemberLevel.integral)
        : 0;
      const nextLimit = toNumber(this.nextMemberLevel.integral);
      const total = nextLimit - currentLimit;
      if (total <= 0) {
        return 100;
      }
      return Math.round(clamp(((this.memberTotalPoints - currentLimit) / total) * 100, 0, 100));
    },
    teamStats() {
      return {
        people: toNumber(this.teamSummary.people),
        hold: toNumber(this.teamSummary.hold)
      };
    },
    normalizedTeamLevels() {
      return (this.teamLevels || []).map(item => {
        return {
          level: toNumber(item.level),
          name: item.name,
          people: toNumber(item.people),
          hold: toNumber(item.hold),
          reward: toNumber(item.reward),
          reached: toNumber(item.reached)
        };
      });
    },
    currentTeamLevel() {
      if (this.teamSummary.current_level) {
        return this.teamSummary.current_level;
      }
      const reached = this.normalizedTeamLevels
        .filter(item => this.teamStats.people >= item.people && this.teamStats.hold >= item.hold)
        .pop();
      return reached || null;
    },
    nextTeamLevel() {
      if (this.teamSummary.next_level) {
        return this.teamSummary.next_level;
      }
      return this.normalizedTeamLevels.find(item => {
        return this.teamStats.people < item.people || this.teamStats.hold < item.hold;
      });
    },
    currentTeamReward() {
      return this.currentTeamLevel ? toNumber(this.currentTeamLevel.reward) : 0;
    },
    teamGap() {
      if (this.teamSummary.gap) {
        return {
          people: toNumber(this.teamSummary.gap.people),
          hold: toNumber(this.teamSummary.gap.hold)
        };
      }
      if (!this.nextTeamLevel) {
        return { people: 0, hold: 0 };
      }
      return {
        people: Math.max(0, toNumber(this.nextTeamLevel.people) - this.teamStats.people),
        hold: Math.max(0, toNumber(this.nextTeamLevel.hold) - this.teamStats.hold)
      };
    },
    teamGapText() {
      const peopleGap = this.teamGap.people;
      const holdGap = this.teamGap.hold;
      if (peopleGap > 0 && holdGap > 0) {
        return `${this.formatInteger(peopleGap)} 人 / ${this.formatMoney(holdGap)} USD`;
      }
      if (peopleGap > 0) {
        return `${this.formatInteger(peopleGap)} 人`;
      }
      if (holdGap > 0) {
        return `${this.formatMoney(holdGap)} USD 购买额`;
      }
      return "最后一步";
    },
    teamProgress() {
      if (!this.nextTeamLevel) {
        return 100;
      }
      const peoplePercent = toNumber(this.nextTeamLevel.people) > 0
        ? this.teamStats.people / toNumber(this.nextTeamLevel.people)
        : 0;
      const holdPercent = toNumber(this.nextTeamLevel.hold) > 0
        ? this.teamStats.hold / toNumber(this.nextTeamLevel.hold)
        : 0;
      return Math.round(clamp(((peoplePercent + holdPercent) / 2) * 100, 0, 100));
    },
    activeProgress() {
      return this.activeTab === "member" ? this.memberProgress : this.teamProgress;
    },
    activeProgressText() {
      if (this.activeTab === "member") {
        if (!this.nextMemberLevel) {
          return "最高等级";
        }
        return this.memberPointGap > 0
          ? `${this.memberProgress}% 已完成`
          : "已满足升级积分";
      }
      return this.nextTeamLevel ? `${this.teamProgress}% 已完成` : "最高等级";
    }
  },
  created() {
    this.activeTab = this.$route.query.tab === "team" ? "team" : "member";
  },
  mounted() {
    this.fetchUserInfo();
    this.fetchMemberLevels();
    this.fetchCommissionRules();
    this.fetchTeamGrade();
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    switchTab(tab) {
      if (this.activeTab === tab) {
        return;
      }
      this.activeTab = tab;
      this.$router.replace({
        path: this.$route.path,
        query: Object.assign({}, this.$route.query, { tab })
      });
    },
    fetchUserInfo() {
      this.loading.user = true;
      getMyUserInfo()
        .then(res => {
          this.userInfo = res.data.data || {};
        })
        .catch(err => {
          if (err && err.toLogin) {
            return;
          }
          this.$dialog.error(getErrorMessage(err, "会员信息加载失败"));
        })
        .then(() => {
          this.loading.user = false;
        });
    },
    fetchMemberLevels() {
      this.loading.member = true;
      request
        .get("/user/level/list", {}, { login: false })
        .then(res => {
          const list = res.data.data || [];
          this.memberLevels = list
            .map(item => Object.assign({}, item))
            .sort((a, b) => {
              return toNumber(a.level) - toNumber(b.level);
            });
        })
        .catch(err => {
          this.$dialog.error(getErrorMessage(err, "会员等级加载失败"));
        })
        .then(() => {
          this.loading.member = false;
        });
    },
    fetchCommissionRules() {
      request
        .get("/user/user/invite", {}, { login: false })
        .then(res => {
          const data = res.data.data || {};
          this.commissionRules = [
            { level: 1, label: "1代", rate: data.l1, desc: "直推会员购买" },
            { level: 2, label: "2代", rate: data.l2, desc: "下级邀请购买" },
            { level: 3, label: "3代", rate: data.l3, desc: "三级团队购买" }
          ].map((item, index) => {
            const fallback = FALLBACK_COMMISSION_RULES[index];
            const rate = toOptionalNumber(item.rate);
            return Object.assign({}, item, {
              rate: rate === null ? fallback.rate : rate
            });
          });
        })
        .catch(() => {
          this.commissionRules = FALLBACK_COMMISSION_RULES;
        });
    },
    fetchTeamGrade() {
      this.loading.team = true;
      request
        .get("/user/team/grade", {}, { login: true })
        .then(res => {
          const data = res.data.data || {};
          this.teamSummary = Object.assign({}, this.teamSummary, data);
          this.teamLevels = data.levels && data.levels.length
            ? data.levels
            : FALLBACK_TEAM_LEVELS;
        })
        .catch(err => {
          if (err && err.toLogin) {
            return;
          }
          this.$dialog.error(getErrorMessage(err, "团队等级加载失败"));
        })
        .then(() => {
          this.loading.team = false;
        });
    },
    displayLevelName(item) {
      if (item && item.name) {
        return item.name;
      }
      return `VIP${item ? item.level : 0}`;
    },
    getMemberBuildPoints(item) {
      if (!item) {
        return 0;
      }
      const fields = [
        item.build_integral,
        item.open_position_integral,
        item.position_integral,
        item.integral
      ];
      for (let i = 0; i < fields.length; i++) {
        const value = toOptionalNumber(fields[i]);
        if (value !== null) {
          return value;
        }
      }
      return 0;
    },
    displayBadgeName(item) {
      if (toNumber(item && item.level) === 0) {
        return "普通";
      }
      return this.displayLevelName(item);
    },
    getMemberSignReward(item) {
      if (!item) {
        return 0;
      }
      const fields = [
        item.sign_reward,
        item.daily_sign_reward,
        item.sign,
        item.sign_in
      ];
      for (let i = 0; i < fields.length; i++) {
        const value = toOptionalNumber(fields[i]);
        if (value !== null) {
          return value;
        }
      }
      return 0;
    },
    isCurrentMember(item) {
      return this.currentMemberLevel && toNumber(this.currentMemberLevel.level) === toNumber(item.level);
    },
    isMemberReached(item) {
      return this.currentMemberNumber >= toNumber(item.level);
    },
    isCurrentTeam(item) {
      return this.currentTeamLevel && toNumber(this.currentTeamLevel.level) === toNumber(item.level);
    },
    isTeamReached(item) {
      return (
        item.reached === 1 ||
        (this.teamStats.people >= toNumber(item.people) &&
          this.teamStats.hold >= toNumber(item.hold))
      );
    },
    teamRuleStatus(item) {
      if (this.isCurrentTeam(item)) {
        return "当前等级";
      }
      return this.isTeamReached(item) ? "已达成" : "待达成";
    },
    formatInteger(value) {
      return String(Math.floor(toNumber(value))).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatMoney(value) {
      return toNumber(value)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatMoneyCompact(value) {
      return this.formatMoney(value);
    },
    formatPercent(value) {
      return toNumber(value).toFixed(2);
    }
  }
};
</script>

<style scoped lang="less">
.grade-page {
  min-height: 100vh;
  padding-bottom: 74px;
  background: #f5f7fb;
  color: #172033;
}

.grade-page,
.grade-page * {
  box-sizing: border-box;
}

.grade-page /deep/ .van-nav-bar {
  background: #0d7df2;
  box-shadow: none;
}

.grade-page /deep/ .van-nav-bar__title,
.grade-page /deep/ .van-icon {
  color: #fff;
}

.grade-shell {
  padding: 12px 12px 18px;
}

.grade-hero {
  padding: 16px;
  border-radius: 8px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(135deg, rgba(8, 110, 221, 0.96), rgba(14, 168, 229, 0.92)),
    #0d7df2;
  box-shadow: 0 12px 28px rgba(14, 110, 210, 0.18);
}

.hero-top {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 6px;
  align-items: center;
  min-width: 0;
}

.avatar-mark {
  width: 54px;
  height: 54px;
  grid-row: 1 / span 2;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: #fff;
  color: #bc8527;
  font-size: 18px;
  font-weight: 800;
  box-shadow: inset 0 0 0 1px rgba(188, 133, 39, 0.18);
}

.hero-user {
  min-width: 0;
  flex: 1 1 0;
}

.hero-user p {
  margin: 0 0 6px;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
}

.hero-user strong {
  display: block;
  min-height: 18px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-chip {
  height: 28px;
  grid-column: 2;
  justify-self: start;
  max-width: 76px;
  padding: 0 9px;
  border-radius: 8px;
  line-height: 28px;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.16);
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 17px;
}

.hero-metrics div {
  min-width: 0;
  padding: 10px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
}

.hero-metrics span {
  display: block;
  color: rgba(255, 255, 255, 0.72);
  font-size: 11px;
  line-height: 16px;
}

.hero-metrics strong {
  display: block;
  margin-top: 4px;
  min-height: 22px;
  overflow: hidden;
  color: #fff;
  font-size: 16px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-reward {
  display: block;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.13);
}

.hero-reward div {
  min-width: 0;
}

.hero-reward span {
  display: block;
  color: rgba(255, 255, 255, 0.74);
  font-size: 11px;
  line-height: 16px;
}

.hero-reward strong {
  display: block;
  margin-top: 2px;
  color: #fff6d8;
  font-size: 17px;
  line-height: 24px;
  font-weight: 900;
  white-space: nowrap;
}

.hero-reward em {
  display: block;
  max-width: none;
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  line-height: 18px;
  font-style: normal;
  text-align: left;
}

.progress-wrap {
  margin-top: 15px;
}

.progress-track {
  height: 7px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.25);
}

.progress-fill {
  height: 100%;
  border-radius: 8px;
  background: linear-gradient(90deg, #ffe0a3, #ffffff);
  transition: width 0.28s ease;
}

.progress-copy {
  margin-top: 7px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  line-height: 17px;
  text-align: right;
}

.grade-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 12px 0;
  padding: 4px;
  border-radius: 8px;
  background: #e9edf5;
}

.grade-tabs button {
  height: 42px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #667085;
  font-size: 14px;
  line-height: 42px;
  font-weight: 800;
}

.grade-tabs button.active {
  background: #fff;
  color: #0d7df2;
  box-shadow: 0 7px 18px rgba(16, 70, 122, 0.1);
}

.commission-panel {
  margin-bottom: 12px;
  padding: 14px;
  border-radius: 8px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(135deg, rgba(23, 32, 51, 0.96), rgba(35, 62, 88, 0.94)),
    #172033;
  box-shadow: 0 12px 28px rgba(23, 32, 51, 0.14);
}

.commission-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.commission-head div {
  min-width: 0;
}

.commission-head span {
  display: block;
  color: #ffe4aa;
  font-size: 17px;
  line-height: 24px;
  font-weight: 900;
}

.commission-head strong {
  display: block;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
}

.commission-head > em {
  height: 28px;
  min-width: 52px;
  border-radius: 8px;
  background: #f4b84b;
  color: #172033;
  font-size: 12px;
  line-height: 28px;
  text-align: center;
  font-style: normal;
  font-weight: 900;
}

.commission-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 13px;
}

.commission-step {
  min-width: 0;
  padding: 10px 7px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.1);
}

.commission-step span,
.commission-step em {
  display: block;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  line-height: 16px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.commission-step strong {
  display: block;
  margin: 3px 0 2px;
  color: #7bf0be;
  font-size: 18px;
  line-height: 24px;
  font-weight: 900;
}

.grade-panel {
  padding: 14px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(23, 32, 51, 0.06);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.panel-head > div {
  min-width: 0;
  flex: 1 1 0;
}

.panel-head p {
  margin: 0;
  color: #172033;
  font-size: 17px;
  line-height: 24px;
  font-weight: 900;
}

.panel-head strong {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: #7b8494;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-head span {
  height: 28px;
  min-width: 48px;
  flex: 0 0 auto;
  padding: 0 9px;
  border-radius: 8px;
  background: #f5efe3;
  color: #936622;
  font-size: 12px;
  line-height: 28px;
  text-align: center;
  font-weight: 800;
}

.empty-state {
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #7b8494;
  font-size: 13px;
}

.member-list,
.team-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.member-row {
  min-height: 64px;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
  row-gap: 8px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e9edf5;
  background: #fff;
}

.member-row.reached {
  border-color: #d7edf5;
  background: #f6fbfd;
}

.member-row.current {
  border-color: #f0d59c;
  background: #fffaf0;
}

.level-badge {
  width: 48px;
  height: 40px;
  grid-row: 1 / span 2;
  flex: 0 0 auto;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #172033;
  color: #ffd996;
  font-size: 13px;
  font-weight: 900;
}
.level-sign-highlight {
  min-width: 92px;
  padding: 7px 9px;
  text-align: right;
  background: #fff5dd;
  border: 1px solid #f2dca9;
  border-radius: 9px;
}
.level-sign-highlight span { display: block; color: #9a793d; font-size: 9px; line-height: 13px; }
.level-sign-highlight strong { display: block; margin-top: 2px; color: #9f6315; font-size: 14px; line-height: 18px; }
.level-sign-highlight em { font-size: 9px; font-style: normal; }

.level-info {
  min-width: 0;
  flex: 1 1 0;
}

.level-info strong {
  display: block;
  color: #172033;
  font-size: 15px;
  line-height: 21px;
}

.level-info span {
  display: block;
  margin-top: 2px;
  color: #7b8494;
  font-size: 12px;
  line-height: 17px;
}

.level-benefits {
  width: 100%;
  grid-column: 2 / 4;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.benefit-cell {
  min-width: 0;
  min-height: 52px;
  padding: 7px 6px;
  border-radius: 8px;
  border: 1px solid #eef1f6;
  background: #f7f9fc;
}

.benefit-cell span {
  display: block;
  overflow: hidden;
  color: #7b8494;
  font-size: 10px;
  line-height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.benefit-cell strong {
  display: block;
  margin-top: 4px;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.benefit-cell em {
  font-size: 10px;
  font-style: normal;
}

.rate-cell {
  border-color: #dcedff;
  background: #eef7ff;
}

.rate-cell strong {
  color: #0d7df2;
}

.points-cell {
  border-color: #dff0e9;
  background: #effbf6;
}

.points-cell strong {
  color: #16845c;
}

.sign-cell {
  border-color: #f8e8c7;
  background: #fff8e8;
}

.sign-cell strong {
  color: #a06618;
}

.team-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-bottom: 12px;
}

.team-summary div {
  min-width: 0;
  padding: 12px 10px;
  border-radius: 8px;
  background: #f6f8fc;
}

.team-summary span,
.rule-grid span {
  display: block;
  color: #7b8494;
  font-size: 12px;
  line-height: 17px;
}

.team-summary strong,
.rule-grid strong {
  display: block;
  margin-top: 4px;
  color: #172033;
  font-size: 14px;
  line-height: 20px;
  font-weight: 900;
  word-break: break-word;
}

.team-rule {
  padding: 12px;
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fff;
}

.team-rule.reached {
  border-color: #d7edf5;
  background: #f7fcfd;
}

.team-rule.current {
  border-color: #f0d59c;
  background: #fffaf0;
}

.rule-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.rule-index {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0d7df2;
  color: #fff;
  font-size: 16px;
  font-weight: 900;
}

.rule-main > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.rule-main strong {
  display: block;
  color: #172033;
  font-size: 15px;
  line-height: 21px;
}

.rule-main span {
  display: block;
  margin-top: 1px;
  color: #7b8494;
  font-size: 12px;
  line-height: 17px;
}

.rule-main em {
  min-width: 86px;
  flex: 0 0 auto;
  color: #a06618;
  font-size: 12px;
  line-height: 17px;
  font-style: normal;
  text-align: right;
  font-weight: 900;
}

.rule-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 8px;
  margin-top: 11px;
}

.rule-grid div {
  min-width: 0;
  padding: 9px 7px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(228, 233, 242, 0.9);
}

.rule-grid strong {
  font-size: 13px;
  line-height: 18px;
}
</style>

